import { Plugins } from '@leverege/plugin';
import Attributes from "./Attributes.js";
/**
 * A class that will sort the given plugin type by name, objectType and valueType.
 * To use this, call:
 *   const view = new NameValueView( 'AttributeEditor', { handleKey : 'canEdit' } )
 * To get an item, you can call:
 *   const attributeItem = view.getItemFor( attrName, objRef )
 * 
 * The acquistion of an item is ordered in this manner:
 * 1) name + objectType : install a specific item for the named attribute for a specific object type
 * 2) name : install a specific item for the named attribute for all object types
 * 3) valueType + objectType : install a specific item for the value type for a specific object type
 * 4) valueType : install a specific item for the value type
 */
export default class NameValueView {
  constructor(pluginType, options = {}) {
    this.view = Plugins.createView(pluginType, {
      sort: numSortComparator,
      processor: processor.bind(this)
    });
    this.options = options;
    this.handleKey = options?.handleKey;
    this.nameKey = options?.nameKey || 'name';
    this.multiple = options?.multiple || false;
    this.defaultValue = options?.defaultValue || null;
  }
  getItemFor = (name, objRefOrType) => {
    const type = typeof objRefOrType === 'string' ? objRefOrType : objRefOrType?.type;
    const {
      nameItems,
      valueItems
    } = this.view.get();
    const filter = item => {
      if (this.handleKey == null || item[this.handleKey] === undefined) {
        return true;
      }
      if (typeof item[this.handleKey] === 'function' && item[this.handleKey](objRefOrType, name)) {
        return true;
      }
      return false;
    };
    const filterType = this.multiple ? 'filter' : 'find';

    // Look for an name/objectType item first, then a name one
    let item = nameItems?.get(keyFor(name, type))?.[filterType](filter);
    item = item || nameItems?.get(keyFor(name))?.[filterType](filter);
    if (item) {
      return item;
    }
    // Look for a valueType/objectType one, then a valueType one
    const attr = Attributes.getAttribute(name, type);
    if (attr && attr.valueType) {
      item = item || valueItems?.get(keyFor(attr.valueType, type))?.[filterType](filter);
      item = item || valueItems?.get(keyFor(attr.valueType))?.[filterType](filter);
    }
    return item || this.defaultValue;
  };
}
function keyFor(base, type) {
  return type ? `${base}:${type}` : base;
}
function processor(plgs) {
  const nameItems = new Map();
  const valueItems = new Map();
  const registerValueType = (valueType, objectType, plg) => {
    const key = keyFor(valueType, objectType);
    const curr = valueItems.get(key);
    if (curr == null) {
      valueItems.set(key, [plg]);
    } else {
      curr.push(plg);
    }
  };
  for (let n = 0; n < plgs.length; n++) {
    const plg = plgs[n];
    const {
      objectType,
      [this.nameKey]: name,
      valueType
    } = plg;
    if (name) {
      const key = keyFor(name, objectType);
      const curr = nameItems.get(key);
      if (curr == null) {
        nameItems.set(key, [plg]);
      } else {
        curr.push(plg);
      }
    }
    if (valueType) {
      if (Array.isArray(valueType)) {
        valueType.forEach(vType => registerValueType(vType, objectType, plg));
      } else {
        registerValueType(valueType, objectType, plg);
      }
    }
  }
  return {
    nameItems,
    valueItems
  };
}
function numSortComparator(a, b) {
  const an = a.sort || 0;
  const bn = b.sort || 0;
  return an - bn;
}